import * as React from 'react';

import { useFormContext } from 'react-hook-form';

import { Box, Collapse, Divider, Grid } from '@mui/material';

import { CabinEventReport, RadioGroupOption } from '../../../core/api/clients';

import ControlledCascadingSelect from '../../../components/ControlledCascadingSelect';

import ControlledTextField from '../../../components/ControlledTextField';
import ControlledTimeField from '../../../components/ControlledTimeField';
import ControlledDatePicker from '../../../components/ControlledDatePicker';
import ControlledRadioGroup from '../../../components/ControlledRadioGroup';
import ControlledSelect from '../../../components/ControlledSelect';
import { useCabinEventFieldOptions } from '../../../core/context/CabinEventFieldsOptionsContext';
import { useCabinEventReportData } from '../../../core/context/CabinEventReportDataContext';

function EventStep({ visible, readOnly }: { visible: boolean, readOnly?: boolean }) {
  const { eventOptions, phaseOptions } = useCabinEventFieldOptions();
  const { control, setValue, resetField } = useFormContext<CabinEventReport>();
  const { allowedActions } = useCabinEventReportData();

  return (
    <Collapse in={visible}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box display="flex">
            <ControlledTextField<CabinEventReport>
              name="eventTitle"
              control={control}
              rules={{ required: true }}
              readOnly={readOnly}
              disableEditPopper
            />
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <ControlledTextField<CabinEventReport>
            name="locationOfEvent"
            label="Location of Event"
            control={control}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <ControlledDatePicker<CabinEventReport> name="eventDate" control={control} readOnly={readOnly} rules={{ required: true }} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <ControlledTimeField
            name="localEventTime"
            control={control}
            readOnly={readOnly}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12}>
          <ControlledCascadingSelect<CabinEventReport>
            groupName="eventType"
            name="eventType"
            control={control}
            fieldNames={['eventType', 'eventType2', 'eventType3']}
            options={eventOptions}
            rules={{ required: true }}
            readOnly={readOnly}
            setValue={setValue}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
      </Grid>
      <Divider sx={{ my: 2 }} />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <ControlledSelect<CabinEventReport>
            name="typeOfFlight"
            label="Type of Flight"
            control={control}
            menuItems={[
              { name: 'Revenue (part 121)', value: 'Revenue (part 121)' },
              { name: 'Non-Revenue (part 91)', value: 'Non-Revenue (part 91)' },
              { name: 'Maintenance Test', value: 'Maintenance Test' },
              { name: 'Special Flight Permit', value: 'Special Flight Permit' },
            ]}
            readOnly={readOnly}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ControlledTextField<CabinEventReport>
            name="numberOfPassengers"
            label="Number of Passengers"
            control={control}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
            TextFieldProps={{
              inputProps: {
                onBlur: (event) => {
                  const { value } = event.target;
                  const nan = Number.isNaN(Number(value));
                  if (value === '') {
                    setValue('numberOfPassengers', null);
                  } else if (nan) {
                    resetField('numberOfPassengers', {
                      keepDirty: false,
                      keepError: false,
                      keepTouched: false,
                    });
                  }
                },
              },
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <ControlledCascadingSelect<CabinEventReport>
            groupName="phaseOfFlight"
            name="phaseOfFlight"
            control={control}
            fieldNames={['phaseOfFlight', 'phaseOfFlight2']}
            options={phaseOptions}
            rules={{ required: true }}
            readOnly={readOnly}
            setValue={setValue}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ControlledSelect<CabinEventReport>
            name="timeOfDay"
            control={control}
            readOnly={readOnly}
            menuItems={[
              { name: 'Sunrise', value: 'Sunrise' },
              { name: 'Day', value: 'Day' },
              { name: 'Sunset', value: 'Sunset' },
              { name: 'Night', value: 'Night' },
            ]}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ControlledSelect<CabinEventReport>
            name="cabinLighting"
            control={control}
            readOnly={readOnly}
            menuItems={[
              { name: 'High', value: 'High' },
              { name: 'Medium', value: 'Medium' },
              { name: 'Low', value: 'Low' },
              { name: 'Off', value: 'Off' },
            ]}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
      </Grid>
      <Divider sx={{ my: 2 }} />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<CabinEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="Emergency Declared"
            name="emergencyDeclared"
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            readOnly={readOnly}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<CabinEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="Cabin Braced"
            name="cabinBraced"
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            readOnly={readOnly}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<CabinEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="Seat Belt Sign"
            name="seatBeltSign"
            radioOptions={[
              { label: 'On', value: RadioGroupOption.Yes },
              { label: 'Off', value: RadioGroupOption.No },
            ]}
            readOnly={readOnly}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<CabinEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="EMS Requested"
            name="emsRequested"
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            readOnly={readOnly}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<CabinEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="EMS Responded"
            name="emsResponded"
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            readOnly={readOnly}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<CabinEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="Security Requested"
            name="securityRequested"
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            readOnly={readOnly}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<CabinEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="Security Responded"
            name="securityResponded"
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            readOnly={readOnly}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<CabinEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="Emergency Med Kit Used"
            name="emergencyMedKitUsed"
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            readOnly={readOnly}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<CabinEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="AED Used"
            name="aedUsed"
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            readOnly={readOnly}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<CabinEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="Portable Oxygen Used"
            name="portableOxygenUsed"
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            readOnly={readOnly}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<CabinEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="Fire Extinguisher Used"
            name="fireExtinguisherUsed"
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            readOnly={readOnly}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <ControlledRadioGroup<CabinEventReport>
            RadioGroupProps={{ row: true }}
            control={control}
            label="Aircraft Taxi to Gate"
            name="aircraftTaxiToGate"
            radioOptions={[
              { label: 'Yes', value: RadioGroupOption.Yes },
              { label: 'No', value: RadioGroupOption.No },
            ]}
            readOnly={readOnly}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
      </Grid>
    </Collapse>
  );
}

export default EventStep;
